import React, { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";
import { Appliance } from "../../../../../model/appliance";
import { CombinedAppliance } from "../../../../../model/combined-appliance-category";
import { useLazyGetApplianceCategoriesQuery } from "../../../../../service/appliance-category";
import { setAppliances } from "../../../../../stores/slices/get-solar-slice";
import { RootState } from "../../../../../stores/store";
import { EnergyRequirementsSubView, SelectedApplianceConfig } from "./energy-requirements.types";
import { EnergyRequirementsLanding } from "./EnergyRequirementsLanding";
import { SelectAppliance } from "./SelectAppliance";
import { ConfigureAppliance } from "./ConfigureAppliance";
import { ApplianceSummary } from "./ApplianceSummary";

interface EnergyRequirementsProps {
	onCancel: () => void;
	onContinue: () => void;
	onHeaderChange: (title: string, onBack: () => void) => void;
}

export const EnergyRequirements: React.FC<EnergyRequirementsProps> = ({
	onCancel,
	onContinue,
	onHeaderChange,
}) => {
	const dispatch = useDispatch();
	const savedAppliances = useSelector((state: RootState) => state.getSolar.appliances);

	const [subView, setSubView] = useState<EnergyRequirementsSubView>("landing");
	const [selectedAppliances, setSelectedAppliances] = useState<SelectedApplianceConfig[]>(savedAppliances);
	const [currentAppliance, setCurrentAppliance] = useState<Appliance | null>(null);
	const [editingIndex, setEditingIndex] = useState<number | null>(null);

	const [getApplianceCategories, { data: categories = [] }] = useLazyGetApplianceCategoriesQuery();
	const [combinedAppliances, setCombinedAppliances] = useState<CombinedAppliance[]>([]);
	const [isLoading, setIsLoading] = useState(false);

	// Fetch appliance categories (includes nested appliances via Include)
	useEffect(() => {
		const fetchData = async () => {
			setIsLoading(true);
			try {
				await getApplianceCategories();
			} catch {
				toast.error("There was an error fetching the appliances");
			} finally {
				setIsLoading(false);
			}
		};
		fetchData();
	}, [getApplianceCategories]);

	// Process categories into CombinedAppliance list
	useEffect(() => {
		if (categories.length === 0) return;

		const defaultCategory = categories.find(cat => cat.name.toLowerCase() === "others");
		const otherCategories = categories.filter(cat => cat.name.toLowerCase() !== "others");

		const defaultAppliances = (defaultCategory?.appliances || []).map(
			appliance =>
				new CombinedAppliance({
					id: appliance.id,
					name: appliance.name,
					power: appliance.power,
					iconUrl: appliance.iconUrl,
					applianceCategoryId: appliance.applianceCategoryId,
					applianceCategory: appliance.applianceCategory,
				})
		);

		const categoryAppliances = otherCategories.map(category => {
			const sortedAppliances = [...(category.appliances || [])].sort((a, b) =>
				a.name.localeCompare(b.name)
			);
			return new CombinedAppliance({
				id: category.id,
				name: category.name,
				imageUrl: category.imageUrl,
				description: category.description,
				deleted: category.deleted,
				appliances: sortedAppliances,
			});
		});

		const combined = [...categoryAppliances, ...defaultAppliances].sort((a, b) => {
			if (a.isCategory() && !b.isCategory()) return -1;
			if (!a.isCategory() && b.isCategory()) return 1;
			return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
		});

		setCombinedAppliances(combined);
	}, [categories]);

	// Update header based on sub-view
	useEffect(() => {
		switch (subView) {
			case "landing":
				onHeaderChange("Tell Us Your Energy Requirements", onCancel);
				break;
			case "select-appliance":
				onHeaderChange("Select Appliance", () => setSubView("landing"));
				break;
			case "configure-appliance":
				onHeaderChange("Select Quantity & Usage", () => setSubView("select-appliance"));
				break;
			case "summary":
				onHeaderChange("Select Appliance", () => setSubView("landing"));
				break;
		}
	}, [subView, onCancel, onHeaderChange]);

	const handleAddAppliance = useCallback(() => {
		setEditingIndex(null);
		setCurrentAppliance(null);
		setSubView("select-appliance");
	}, []);

	const handleSelectAppliance = useCallback((appliance: Appliance) => {
		setCurrentAppliance(appliance);
		setEditingIndex(null);
		setSubView("configure-appliance");
	}, []);

	const handleEditAppliance = useCallback((index: number) => {
		const config = selectedAppliances[index];
		const appliance = new Appliance({
			id: config.applianceId,
			name: config.applianceName,
			power: config.appliancePower,
			iconUrl: config.applianceIconUrl,
			applianceCategoryId: "",
			applianceCategory: "",
		});
		setCurrentAppliance(appliance);
		setEditingIndex(index);
		setSubView("configure-appliance");
	}, [selectedAppliances]);

	const handleRemoveAppliance = useCallback((index: number) => {
		setSelectedAppliances(prev => prev.filter((_, i) => i !== index));
	}, []);

	const handleSaveConfig = useCallback((config: SelectedApplianceConfig) => {
		if (editingIndex !== null) {
			setSelectedAppliances(prev =>
				prev.map((item, i) => (i === editingIndex ? config : item))
			);
		} else {
			setSelectedAppliances(prev => [...prev, config]);
		}
		setCurrentAppliance(null);
		setEditingIndex(null);
		setSubView("select-appliance");
	}, [editingIndex]);

	const handleContinue = useCallback(() => {
		setSubView("summary");
	}, []);

	const handleSave = useCallback(() => {
		dispatch(setAppliances(selectedAppliances));
		onContinue();
	}, [dispatch, selectedAppliances, onContinue]);

	return (
		<>
			{subView === "landing" && (
				<EnergyRequirementsLanding
					selectedAppliances={selectedAppliances}
					onAddAppliance={handleAddAppliance}
					onEditAppliance={handleEditAppliance}
					onRemoveAppliance={handleRemoveAppliance}
					onCancel={onCancel}
					onContinue={handleContinue}
				/>
			)}

			{subView === "select-appliance" && (
				<SelectAppliance
					appliances={combinedAppliances}
					selectedAppliances={selectedAppliances}
					isLoading={isLoading}
					onSelectAppliance={handleSelectAppliance}
					onCancel={() => setSubView("landing")}
					onContinue={() => setSubView("landing")}
				/>
			)}

			{subView === "configure-appliance" && currentAppliance && (
				<ConfigureAppliance
					appliance={currentAppliance}
					existingConfig={editingIndex !== null ? selectedAppliances[editingIndex] : undefined}
					onSave={handleSaveConfig}
					onCancel={() => setSubView("select-appliance")}
				/>
			)}

			{subView === "summary" && (
				<ApplianceSummary
					selectedAppliances={selectedAppliances}
					onAddAnother={handleAddAppliance}
					onCancel={onCancel}
					onSave={handleSave}
				/>
			)}
		</>
	);
};
